import { GeneralApiService } from '@api_patient/general_api.service';
import { UnitsApiService } from '@api_patient/units_api.service';
import { AuthService } from '@application/authentication/auth.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { UniversalConfig } from '@application/universal_config.service';
import {
	AsyncSignalValue,
	asyncSignalAccessible,
	asyncSignalError,
	asyncSignalIdle,
	asyncSignalPending,
} from '@cortex-shim/async_signal.component';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { signal } from '@preact/signals-react';
import type { AppConfigInfo, UnitAboutApiModel } from '@visiba/api-vcs-client-v2';
import { ControllerProtocol, Injectable, Scopes } from '@visiba/instantiation';

export interface AddressField {
	key: string;
	value: string;
}

export interface LegalField {
	key: string;
	translationKey: I18nAndL10n.LocaleKey;
	value: string;
}

export interface ExternalLink {
	name: string | null;
	url: string;
}

interface Data {
	aboutUsText: string | null;
	addressInfo: AddressField[];
	legal: LegalField[];
	externalLink: ExternalLink | null;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class FooterController implements ControllerProtocol {
	public data = signal<AsyncSignalValue<Data>>(asyncSignalIdle());

	constructor(
		private readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
	) {}

	whenMount() {
		this.fetchData();
	}

	private async fetchData() {
		this.data.value = asyncSignalPending();
		const addressInfo: AddressField[] = [];

		const aboutDataResult = await this.unitsApiService.getLicenseHolderAboutData(this.universalConfig.rootUnit);
		if (aboutDataResult.err) {
			this.data.value = asyncSignalError();
			return;
		}

		const aboutData = aboutDataResult.ok.data;

		if (aboutData.VisitAddress?.Address != null) {
			addressInfo.push({
				key: 'Address+Address2',
				value: [aboutData.VisitAddress.Address, aboutData.VisitAddress.Address2].filter(Boolean).join(', '),
			});
		}

		if (aboutData.VisitAddress?.City != null) {
			addressInfo.push({
				key: 'PostalCode+City',
				value: [aboutData.VisitAddress.PostalCode, aboutData.VisitAddress.City].filter(Boolean).join(' '),
			});
		}

		const legal: LegalField[] = [];
		if (this.universalConfig.legal.pp != null) {
			legal.push({
				key: 'pp',
				value: this.universalConfig.legal.pp,
				translationKey: 'PW.Component.Footer.PP',
			});
		}
		if (this.universalConfig.legal.tos != null) {
			legal.push({
				key: 'tos',
				value: this.universalConfig.legal.tos,
				translationKey: 'PW.Component.Footer.ToS',
			});
		}
		if (!this.universalConfig.disableTrackingCookies) {
			legal.push({
				key: 'Cookie-Policy',
				value: `${this.universalConfig.alias}/cookie-policy`,
				translationKey: 'PW.Component.Footer.CookiePolicy',
			});
		}

		const aboutTextResult = await this.unitsApiService.getTexts(this.universalConfig.rootUnit, ['Web_About']);
		if (aboutTextResult.err) {
			this.data.value = asyncSignalError();
			return;
		}

		this.data.value = asyncSignalAccessible({
			aboutUsText: aboutTextResult.ok.data.Web_About,
			addressInfo,
			legal: legal,
			externalLink:
				aboutData.Url != null
					? {
							name: aboutData.UrlLinkName,
							url: aboutData.Url,
						}
					: null,
		});
	}
}
